import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Collapse, makeStyles } from '@material-ui/core';
import _ from 'lodash';
import { withAnalytic } from '@skooldio/analytics-provider/dist/Analytics/hoc';
import { NetworkStatus } from 'apollo-boost';

import { Flex, Box, Icon, ICON_LIST, Text, Image } from 'Components/Base';
import Breadcrumb from 'Components/Breadcrumb';
import ButtonIcon from 'Components/ButtonIcon';
import Loading from 'Components/Loading';
import AttendanceFilter, { getFilterData, convertToBreadCrumb } from 'Containers/AttendanceFilter';
import AttendanceFilterData from 'Domains/AttendanceFilterData';
import QuizFeedReportData from 'Domains/QuizFeedReportData';
import { useAnalytics } from 'Libs/Analytics';
import * as mixpanel from 'config/mixpanel';
import { SYLLABUS_MODE } from 'Domains/syllabus';

import { updatedFilter } from '../../Containers/AttendanceFilter/utils';

import AdaptiveQuizTable from './AdaptiveQuizTable';
import { getAdaptiveQuizFilterValue } from './utils';

const useStyles = makeStyles({
  container: {
    position: 'absolute',
    top: '50px',
    zIndex: 5,
    minWidth: '350px',
    width: '100%',
    maxWidth: '700px',
  },
});

const FilterBlock = styled(Box)`
  border-radius: 15px;
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.15);
`;

const AdaptiveQuizTableWrapper = styled(Box)`
  position: relative;
  overflow: auto;
  & > div {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    overflow: visible;
  }
`;

const useQueryString = () => {
  return new URLSearchParams(useLocation().search);
};

const EmptyMessage = ({ message }) => (
  <AdaptiveQuizTable
    data={{ reports: [] }}
    renderEmpty={({ colSpan }) => (
      <tr>
        <td colSpan={colSpan}>
          <Box bg={'tableRowBg'} py={9}>
            <Text style={{ textAlign: 'center' }}>{message}</Text>
          </Box>
        </td>
      </tr>
    )}
  />
);

const SelectFilterMessage = ({ translation }) => (
  <AdaptiveQuizTable
    data={{ reports: [] }}
    renderEmpty={({ colSpan }) => (
      <tr>
        <td colSpan={colSpan}>
          <Box bg={'tableRowBg'}>
            <Flex flexDirection={'column'} py={9}>
              <Flex mb={3} mx={'auto'}>
                <Text>{translation('SELECT_BUTTON')}</Text>
              </Flex>
              <Flex mb={3} mx={'auto'}>
                <Image
                  maxHeight={'40px'}
                  src={'/assets/components/Icons/filter-button-icon.png'}
                  srcSet={
                    '/assets/components/Icons/filter-button-icon.png 1x, /assets/components/Icons/filter-button-icon@2x.png 2x, /assets/components/Icons/filter-button-icon@3x.png 3x'
                  }
                />
              </Flex>
              <Flex mb={3} mx={'auto'}>
                <Text>{translation('TO_DISPLAYED')}</Text>
              </Flex>
            </Flex>
          </Box>
        </td>
      </tr>
    )}
  />
);

const AdaptiveQuizTableView = ({ currentFilter, data, translation, setDisabledFetch, mode }) => {
  const isFiltered = !_.isEmpty(currentFilter);
  const report = data?.foQuizFeedReport;
  const isSupported = mode === SYLLABUS_MODE.TUNGKAI && report?.isSupported !== false;

  useEffect(() => {
    // report is undefined when the selected lesson is not in the lesson filter
    setDisabledFetch(!isFiltered || !isSupported || !report);
  }, [isFiltered, isSupported, report, setDisabledFetch]);

  if (!isFiltered) return <SelectFilterMessage translation={translation} />;
  if (!isSupported) return <EmptyMessage message={translation('NOT_SUPPORTED')} />;
  if (_.isEmpty(report?.reports)) return <EmptyMessage message={translation('NOT_FOUND')} />;
  return <AdaptiveQuizTable data={report} />;
};

const AdaptiveQuiz = ({
  isFetchData,
  setIsFetchData,
  setDisabledFetch,
  onLogAction,
  schoolCode,
  myProfileId,
}) => {
  const { send } = useAnalytics();
  const { t } = useTranslation('adaptiveQuiz');
  const queryString = useQueryString();
  const currentPath = useLocation().pathname;
  const history = useHistory();
  const classes = useStyles();
  const [openFilter, setOpenFilter] = useState(false);
  const [currentFilter, setCurrentFilter] = useState({});
  const [isFirstTime, setIsFirstTime] = useState(true);

  const queryFilterValue = getAdaptiveQuizFilterValue(queryString);
  const filterValue = {
    term: currentFilter?.term?.id ?? queryFilterValue.term,
    grade: currentFilter?.grade?.id ?? queryFilterValue.grade,
    classNo: currentFilter?.classNo?.classNo ?? queryFilterValue.classNo,
    subject: currentFilter?.subject?.id ?? queryFilterValue.subject,
    syllabus: currentFilter?.syllabus?.courseCode ?? queryFilterValue.syllabus,
    // lesson ID of the lesson filter, its name is looked up by `useLessonName` where it is needed
    lesson: currentFilter?.lesson?.sectionCode ?? queryFilterValue.lesson,
    mode: currentFilter?.mode ?? queryFilterValue.mode,
  };

  const handleChange = () => {
    setOpenFilter(prev => !prev);
  };

  const handleFilter = ({ action, filterData, queryString }) => {
    onLogAction({
      category: 'Filter',
      action: `Click`,
      label: `Click ${action} filter on adaptive quiz page`,
    });
    send(`${action}_filter`, { on: 'adaptive_quiz_page' });
    setCurrentFilter(filterData);
    setOpenFilter(false);
    if (action === 'submit') {
      mixpanel.trackEvent('Adaptive Quiz Filter Submit Click', {
        distinct_id: myProfileId,
        'Year Term': filterData?.term?.name,
        Grade: filterData?.grade?.name,
        Room: filterData?.classNo?.classNo,
        Subject: filterData?.subject?.name,
        Course: filterData?.syllabus?.courseName,
        Section: filterData?.lesson?.sectionName,
      });
    }
    history.push(`${currentPath}${queryString}`);
  };

  return (
    <>
      <Box>
        <AttendanceFilterData
          initialValue={filterValue}
          schoolCode={schoolCode}
          useLearningResultLessons
        >
          {({ list, value, updateValue }) => {
            const { term, grade, classNo, subject, syllabus, lesson, mode } = value;
            const { termList, gradeList, classList, subjectList, syllabusList, lessonList } = list;

            if (queryString.toString() !== '' && isFirstTime) {
              if (
                termList &&
                gradeList &&
                classList &&
                subjectList &&
                syllabusList &&
                ((syllabus && lessonList) || !syllabus)
              ) {
                if (term && grade && classNo && subject && syllabus && lesson) {
                  setCurrentFilter(getFilterData(list, value));
                }
                setIsFirstTime(false);
              }
            } else {
              updatedFilter(list, value, updateValue, false);
            }

            const handleDisableFilterButton = action => {
              const values = Object.values(value);
              return action === 'submit' ? values.some(v => !v) : values.every(v => !v);
            };

            return (
              <Flex alignItems="center" style={{ position: 'relative' }}>
                <ButtonIcon
                  flex="0 0 auto"
                  variant={'primary'}
                  textProp={{
                    fontWeight: 'semibold',
                  }}
                  lineHeight={'0.5'}
                  height={'40px'}
                  leftIcon={<Icon name={ICON_LIST.FILTER} />}
                  message={t('FILTER')}
                  onClick={handleChange}
                />
                <Collapse in={openFilter} className={classes.container}>
                  <FilterBlock width={1} p={7} maxWidth={'700px'} bg={'white'}>
                    <AttendanceFilter
                      list={list}
                      updateValue={updateValue}
                      value={value}
                      isAddGradesOption={false}
                      isAddClassNosOption={true}
                      isAddLessonsOption={true}
                      isSyllabusRequired={false}
                      isInvalidFilter={handleDisableFilterButton('submit')}
                      onFilterApply={() =>
                        handleFilter({
                          action: 'submit',
                          filterData: getFilterData(list, value),
                          queryString: `?term=${term}&grade=${grade}&class=${classNo}&subj=${subject}&course=${syllabus}&sect=${lesson}&mode=${mode}`,
                        })
                      }
                      onResetFilter={() =>
                        handleFilter({
                          action: 'reset',
                          filterData: {},
                          queryString: '',
                        })
                      }
                      isDisableResetFilter={handleDisableFilterButton('reset')}
                      translation={t}
                    />
                  </FilterBlock>
                </Collapse>
                <Breadcrumb
                  maxItemsPerRow={3}
                  items={convertToBreadCrumb(currentFilter)}
                  p={0}
                  ml={4}
                />
              </Flex>
            );
          }}
        </AttendanceFilterData>
      </Box>
      <AdaptiveQuizTableWrapper>
        <QuizFeedReportData value={filterValue} schoolCode={schoolCode}>
          {({ loading, error, data, refetch, networkStatus }) => {
            if (loading || networkStatus === NetworkStatus.refetch) return <Loading />;
            if (error) return null;
            if (isFetchData) {
              refetch();
              setIsFetchData(false);
            }
            return (
              <AdaptiveQuizTableView
                currentFilter={currentFilter}
                data={data}
                translation={t}
                setDisabledFetch={setDisabledFetch}
                mode={filterValue.mode}
              />
            );
          }}
        </QuizFeedReportData>
      </AdaptiveQuizTableWrapper>
    </>
  );
};

export default withAnalytic(AdaptiveQuiz);
